import { useState, useEffect, useRef, useMemo } from 'react';
import db from '@/db';
import { isGhostMenuItem } from '@/lib/ghostMenuBlacklist';
import { fetchDeliveryPaused, setDeliveryPausedEverywhere } from '@/lib/deliveryPause';
import { getAdminToken, adminLogin } from '@/lib/adminAuth';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/lib/formatters';
import { uploadMenuImageSmart, isBase64Image, formatBytes } from '@/lib/image';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Upload, Image as ImageIcon, Flame, Star, Sparkles, ChefHat, PauseCircle, PlayCircle, UtensilsCrossed, Layers, AlertTriangle, Settings2, X, Users, BarChart3, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

/**
 * Categorias oficiais do cardápio Kaven. A ORDEM aqui define a ordem de
 * exibição das seções na listagem (com ícone). O VALOR é exatamente o que
 * vai gravado em `menuItems.category` — não muda capitalização.
 */
const CATEGORIES = [
  { value: 'Executivo Premium',  label: '🍛 Executivo Premium' },
  { value: 'Churrasco Box',      label: '🥩 Churrasco Box' },
  { value: 'Massas & Cremosos',  label: '🍝 Massas & Cremosos' },
  { value: 'Família',            label: '👨‍👩‍👧‍👦 Família' },
  { value: 'Bebidas',            label: '🥤 Bebidas' },
];
const CATEGORY_VALUES = CATEGORIES.map((c) => c.value);
const FALLBACK_CATEGORY_LABEL = '📦 Outras';

const TAG_OPTIONS = [
  { value: 'mais-pedido', label: 'Mais Pedido', icon: Flame, color: 'bg-orange-500' },
  { value: 'prato-do-dia', label: 'Prato do Dia', icon: Star, color: 'bg-amber-500' },
  { value: 'novo', label: 'Novo', icon: Sparkles, color: 'bg-emerald-500' },
  { value: 'sugestao-chef', label: 'Sugestão do Chef (Cross-Sell)', icon: ChefHat, color: 'bg-[#2563eb]' },
];

const emptyForm = { name: '', category: 'Executivo Premium', description: '', price: '', servings: '', imageUrl: '', tags: [], available: true, presets: ['Padrão'] };

/**
 * Hotfix v7.3 — Firebase RTDB serializa arrays JS como objetos com
 * chaves numéricas (`{0:'a', 1:'b'}`) quando volta via snapshot. Sem
 * normalizar, `.includes/.filter/.map/.length` crashariam aqui também.
 */
function asArray(value, fallback = []) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  if (typeof value === 'string' && value.trim()) return [value];
  return fallback;
}

export default function MenuAdminPage() {
  const { getSessionPin } = useAuth();
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const [presets, setPresets] = useState([]);
  const [activePreset, setActivePreset] = useState('Padrão');
  const [pendingPreset, setPendingPreset] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  // v7.52 — progresso real de upload: { stage: 'optimizing'|'uploading', pct: 0..100 }
  const [uploadProgress, setUploadProgress] = useState(null);
  // Fase 10 - CRUD de presets
  const [showPresetManager, setShowPresetManager] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [renamingPreset, setRenamingPreset] = useState(null); // {id, oldName, newName}
  const [deletingPreset, setDeletingPreset] = useState(null); // preset object
  const fileInputRef = useRef(null);

  const load = async () => {
    const all = await db.menuItems.toArray();
    // v7.51 — Filtra fantasmas para que admin veja apenas pratos legítimos.
    // Auto-purge no boot já deleta ghosts do Dexie, mas filtro aqui é defesa
    // adicional caso um item entre via sync entre o boot e o load.
    const clean = all.filter((it) => !isGhostMenuItem(it));
    // Ordena por categoria → sortOrder (posição definida pelo admin) → nome.
    setItems(clean.sort((a, b) => a.category.localeCompare(b.category)
      || ((a.sortOrder ?? 9999) - (b.sortOrder ?? 9999))
      || a.name.localeCompare(b.name)));
    const p = await db.config.get('deliveryPaused');
    setPaused(!!p?.value);
    // v7.98 — servidor é a autoridade: sobrescreve o cache local se responder.
    fetchDeliveryPaused().then((v) => { if (v !== null) setPaused(v); });
    const ap = await db.config.get('activePreset');
    setActivePreset(ap?.value || 'Padrão');
    const ps = await db.menuPresets.toArray();
    setPresets(ps);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing({ isNew: true }); setForm(emptyForm); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      category: CATEGORY_VALUES.includes(item.category) ? item.category : 'Executivo Premium',
      description: item.description || '',
      price: String(item.price ?? ''),
      servings: item.servings ? String(item.servings) : '',
      imageUrl: item.imageUrl || '',
      tags: asArray(item.tags),
      available: item.available !== false,
      presets: asArray(item.presets, ['Padrão']).length > 0 ? asArray(item.presets, ['Padrão']) : ['Padrão'],
    });
  };

  const closeDialog = () => { setEditing(null); setForm(emptyForm); };

  const toggleTag = (tag) => {
    setForm((prev) => {
      const has = prev.tags.includes(tag);
      return { ...prev, tags: has ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag] };
    });
  };

  const togglePresetInForm = (presetName) => {
    setForm((prev) => {
      const has = prev.presets.includes(presetName);
      return { ...prev, presets: has ? prev.presets.filter((p) => p !== presetName) : [...prev.presets, presetName] };
    });
  };

  const requestPresetSwitch = (name) => {
    if (name === activePreset) return;
    setPendingPreset(name);
  };

  const confirmPresetSwitch = async () => {
    if (!pendingPreset) return;
    await db.config.put({ key: 'activePreset', value: pendingPreset });
    setActivePreset(pendingPreset);
    toast.success(`Modo "${pendingPreset}" ativado!`);
    setPendingPreset(null);
  };

  // =========================================================
  // CRUD de Presets (Fase 10)
  // =========================================================
  const PRESET_PALETTE = ['#2563eb', '#F59E0B', '#E11D48', '#10b981', '#8b5cf6', '#ec4899', '#f97316'];

  const handleCreatePreset = async () => {
    const name = newPresetName.trim();
    if (!name) return toast.error('Nome do preset é obrigatório');
    if (name.length > 30) return toast.error('Nome muito longo (máx. 30)');
    const existing = await db.menuPresets.where('name').equalsIgnoreCase(name).first();
    if (existing) return toast.error(`Preset "${name}" já existe`);
    const color = PRESET_PALETTE[presets.length % PRESET_PALETTE.length];
    await db.menuPresets.add({ name, description: '', color, protected: false });
    toast.success(`Preset "${name}" criado!`);
    setNewPresetName('');
    load();
  };

  const startRename = (preset) => {
    setRenamingPreset({ id: preset.id, oldName: preset.name, newName: preset.name });
  };

  const handleRenamePreset = async () => {
    if (!renamingPreset) return;
    const { id, oldName, newName } = renamingPreset;
    const cleaned = newName.trim();
    if (!cleaned) return toast.error('Nome inválido');
    if (cleaned === oldName) {
      setRenamingPreset(null);
      return;
    }
    const clash = await db.menuPresets.where('name').equalsIgnoreCase(cleaned).first();
    if (clash && clash.id !== id) return toast.error(`Preset "${cleaned}" já existe`);
    // Atualiza preset + cascade: atualiza menuItems.presets[] + config.activePreset
    await db.menuPresets.update(id, { name: cleaned });
    const allItems = await db.menuItems.toArray();
    for (const it of allItems) {
      const itPresets = asArray(it.presets);
      if (itPresets.includes(oldName)) {
        const newPresets = itPresets.map((p) => (p === oldName ? cleaned : p));
        await db.menuItems.update(it.id, { presets: newPresets });
      }
    }
    const cfg = await db.config.get('activePreset');
    if (cfg?.value === oldName) {
      await db.config.put({ key: 'activePreset', value: cleaned });
      setActivePreset(cleaned);
    }
    toast.success(`Renomeado para "${cleaned}"`);
    setRenamingPreset(null);
    load();
  };

  const confirmDeletePreset = async () => {
    if (!deletingPreset) return;
    const preset = deletingPreset;
    if (preset.protected) {
      toast.error('Não é possível deletar preset padrão');
      setDeletingPreset(null);
      return;
    }
    // Cascade: remove do presets[] de cada menuItem; se item ficar sem preset, atribui ['Padrão']
    const allItems = await db.menuItems.toArray();
    for (const it of allItems) {
      const current = asArray(it.presets);
      const arr = current.filter((p) => p !== preset.name);
      const next = arr.length > 0 ? arr : ['Padrão'];
      if (next.length !== current.length || next.join() !== current.join()) {
        await db.menuItems.update(it.id, { presets: next });
      }
    }
    // Se era o ativo, volta para Padrão
    const cfg = await db.config.get('activePreset');
    if (cfg?.value === preset.name) {
      await db.config.put({ key: 'activePreset', value: 'Padrão' });
      setActivePreset('Padrão');
    }
    await db.menuPresets.delete(preset.id);
    toast.success(`Preset "${preset.name}" removido`);
    setDeletingPreset(null);
    load();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress({ stage: 'optimizing', pct: 0 });
    try {
      // v7.43 — Otimiza (server WebP) → sobe pro Firebase Storage → grava só a URL.
      // Se Storage falhar (offline, sem auth, rules denied, timeout 35s), cai
      // pra base64 inline silenciosamente (modo legado preservado).
      // v7.52 — callback de progresso real (substitui "Processando..." infinito).
      const result = await uploadMenuImageSmart(file, form.name || 'item', (stage, pct) => {
        setUploadProgress({ stage, pct });
      });
      setForm((prev) => ({ ...prev, imageUrl: result.url }));
      const { originalBytes, finalBytes, source, optimizeSource, uploadError } = result;
      const pct = originalBytes > 0
        ? Math.max(0, Math.round((1 - finalBytes / originalBytes) * 100))
        : 0;
      let badge;
      if (source === 'storage') {
        badge = optimizeSource === 'server' ? 'WebP no Storage' : 'JPEG no Storage';
      } else {
        badge = optimizeSource === 'server' ? 'WebP base64 (fallback)' : 'JPEG base64 (fallback)';
      }
      toast.success('Foto otimizada!', {
        description: `${formatBytes(originalBytes)} → ${formatBytes(finalBytes)} ${pct > 0 ? `(−${pct}%)` : ''} · ${badge}${uploadError ? ` (motivo: ${uploadError})` : ''}`,
      });
    } catch (err) {
      toast.error(`Falha ao processar imagem: ${err.message || err}`);
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Nome é obrigatório');
    const priceNum = parseFloat(String(form.price).replace(',', '.'));
    if (!(priceNum >= 0)) return toast.error('Preço inválido');

    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim(),
      price: priceNum,
      servings: form.servings.trim() ? Math.max(1, parseInt(form.servings, 10) || 0) : null,
      imageUrl: form.imageUrl.trim(),
      tags: asArray(form.tags),
      available: form.available,
      presets: asArray(form.presets, ['Padrão']).length > 0 ? asArray(form.presets, ['Padrão']) : ['Padrão'],
    };

    // v7.27 — try/catch + diagnóstico amigável. Sem isso, qualquer falha do
    // IndexedDB (quota cheia, constraint, etc) é silenciosa e o operador
    // só vê "nada aconteceu" no clique.
    try {
      if (editing?.isNew) {
        // v7.46 — Assigna stableId UUID na CRIAÇÃO. Cada prato carrega um
        // identificador estável entre devices que serve de chave para
        // tombstones (em vez do localId Dexie que pode colidir entre devices
        // que criam pratos offline simultaneamente).
        const stableId = (typeof crypto?.randomUUID === 'function')
          ? crypto.randomUUID()
          : `mi-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        await db.menuItems.add({ ...payload, stableId });
        toast.success(`"${payload.name}" cadastrado!`);
      } else {
        await db.menuItems.update(editing.id, payload);
        toast.success(`"${payload.name}" atualizado!`);
      }
      closeDialog();
      load();
    } catch (err) {
      console.error('[MENU SAVE] falhou:', err);
      const name = err?.name || '';
      const msg = err?.message || String(err);
      if (name === 'QuotaExceededError' || /quota/i.test(msg)) {
        toast.error('Espaço local cheio', {
          description: 'Apague pratos antigos ou imagens grandes para liberar espaço.',
          duration: 10000,
        });
      } else if (name === 'ConstraintError') {
        toast.error('Já existe outro prato com esse nome', { description: msg });
      } else {
        toast.error('Falha ao salvar prato', { description: msg, duration: 10000 });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const deletedId = confirmDelete.id;
    const deletedName = confirmDelete.name;
    // UX-fix v7.7 — atualização otimista: remove do state ANTES de aguardar
    // o IndexedDB. Sem isso o card permanece visível 3-5s, dando impressão
    // de que a exclusão falhou e "o item voltou".
    setItems((prev) => prev.filter((it) => it.id !== deletedId));
    setConfirmDelete(null);
    try {
      await db.menuItems.delete(deletedId);
      toast.success(`"${deletedName}" removido`);
    } catch (err) {
      toast.error(`Erro ao excluir: ${err?.message || err}`);
      await load();
      return;
    }
    load();
  };

  const togglePause = async () => {
    const next = !paused;
    setPaused(next);
    // v7.98 — obtém o token admin SILENCIOSAMENTE com o PIN da sessão (o
    // operador já validou o PIN no login); modal só abre se não houver PIN.
    if (!getAdminToken()) {
      const pin = getSessionPin?.();
      if (pin) { try { await adminLogin(pin); } catch { /* cai no modal do authedFetch */ } }
    }
    // grava no backend (autoridade do checkout) + Firebase (tempo real)
    const { serverOk } = await setDeliveryPausedEverywhere(next);
    if (serverOk) {
      toast.success(next ? 'Delivery PAUSADO — novos pedidos bloqueados em todos os aparelhos!' : 'Delivery reaberto para pedidos!');
    } else {
      setPaused(!next);
      try { await db.config.put({ key: 'deliveryPaused', value: !next }); } catch {}
      toast.error('Não foi possível aplicar a pausa no servidor — clientes ainda podem comprar. Verifique a conexão e tente novamente.');
    }
  };

  const toggleAvailable = async (item) => {
    // UX otimista: vira o switch imediatamente para feedback instantâneo.
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, available: !item.available } : it)));
    try {
      await db.menuItems.update(item.id, { available: !item.available });
    } catch (err) {
      toast.error(`Erro: ${err?.message || err}`);
      await load();
      return;
    }
    load();
  };

  // Reordenação de pratos dentro da categoria (estratégia de vendas):
  // move o item pra cima/baixo e re-normaliza sortOrder = índice da lista.
  // O update no Dexie dispara o hook de sync → Firebase → outros devices e /delivery.
  const moveItem = async (sectionItems, idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= sectionItems.length) return;
    const reordered = [...sectionItems];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(target, 0, moved);
    const orderById = new Map(reordered.map((it, i) => [it.id, i]));
    // UI otimista: aplica nova posição imediatamente.
    setItems((prev) => prev
      .map((it) => (orderById.has(it.id) ? { ...it, sortOrder: orderById.get(it.id) } : it))
      .sort((a, b) => a.category.localeCompare(b.category)
        || ((a.sortOrder ?? 9999) - (b.sortOrder ?? 9999))
        || a.name.localeCompare(b.name)));
    try {
      for (const [id, order] of orderById) {
        await db.menuItems.update(id, { sortOrder: order });
      }
    } catch (err) {
      toast.error(`Erro ao reordenar: ${err?.message || err}`);
      await load();
    }
  };

  // v7.14 — useMemo para evitar re-cálculo do groupedByDisplay a cada re-render.
  // Sem isso, 50+ items eram reagrupados em CADA state change (form input, etc),
  // causando frame drops perceptíveis em Android low-end.
  const groupedByDisplay = useMemo(() => {
    const buckets = new Map();
    for (const it of items) {
      const key = CATEGORY_VALUES.includes(it.category) ? it.category : '__uncategorized__';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(it);
    }
    const result = [];
    for (const c of CATEGORIES) {
      const arr = buckets.get(c.value);
      if (arr && arr.length) result.push({ key: c.value, label: c.label, items: arr });
    }
    if (buckets.has('__uncategorized__')) {
      result.push({
        key: '__uncategorized__',
        label: FALLBACK_CATEGORY_LABEL,
        items: buckets.get('__uncategorized__'),
      });
    }
    return result;
  }, [items]);

  // v7.14 — Loading skeleton: enquanto o primeiro load() não terminou,
  // mostra placeholders em vez de "lista vazia" (que confunde).
  const isInitialLoading = items.length === 0 && presets.length === 0;

  return (
    <div data-testid="menu-admin-page" className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">Cardápio</h2>
          <p className="text-sm text-zinc-500 mt-1">Cadastre pratos, preços, fotos e tags sem mexer no código. Use as setas ↑↓ nos cards para definir a ordem de exibição no cardápio.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/cardapio/analytics"
            data-testid="open-analytics-btn"
            className="flex items-center gap-2 bg-white border-2 border-indigo-200 text-indigo-700 rounded-xl font-bold px-5 py-3 min-h-[44px] hover:bg-indigo-50 hover:border-indigo-400 active:translate-y-[1px] transition-all"
          >
            <BarChart3 size={18} /> Performance
          </Link>
          <button
            data-testid="toggle-pause-btn"
            onClick={togglePause}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold min-h-[44px] border-2 transition-all active:translate-y-[1px] ${paused ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100' : 'bg-white border-zinc-300 text-zinc-700 hover:border-zinc-900'}`}
          >
            {paused ? <PlayCircle size={18} /> : <PauseCircle size={18} />}
            {paused ? 'Delivery Pausado — Reabrir' : 'Pausar Delivery'}
          </button>
          <button
            data-testid="new-item-btn"
            onClick={openCreate}
            className="flex items-center gap-2 bg-[#2563eb] text-white rounded-xl font-bold px-5 py-3 min-h-[44px] active:translate-y-[1px] transition-all hover:bg-[#1d4ed8]"
          >
            <Plus size={18} /> Novo Prato
          </button>
        </div>
      </div>

      {paused && (
        <div data-testid="delivery-paused-banner" className="mb-6 flex items-center gap-3 bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <PauseCircle size={22} className="text-red-600 shrink-0" />
          <div>
            <p className="font-bold text-red-800">Delivery pausado</p>
            <p className="text-xs text-red-700">Clientes verão banner "Fechado temporariamente" em /delivery e não poderão finalizar pedidos.</p>
          </div>
        </div>
      )}

      {/* Preset Selector (Fase 5 - Modos de Operação) */}
      <div data-testid="preset-selector" className="mb-6 bg-white border-2 border-zinc-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-[#2563eb]" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Modo de Operação (Preset Ativo)</p>
          </div>
          <button
            data-testid="manage-presets-btn"
            onClick={() => setShowPresetManager(true)}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg border border-zinc-200 bg-white text-xs font-bold text-zinc-600 hover:border-zinc-400 transition-colors"
          >
            <Settings2 size={13} strokeWidth={1.75} /> Gerenciar
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => {
            const active = p.name === activePreset;
            const count = items.filter((i) => asArray(i.presets, ['Padrão']).includes(p.name)).length;
            return (
              <button
                key={p.id}
                data-testid={`preset-chip-${p.name}`}
                onClick={() => requestPresetSwitch(p.name)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm border-2 transition-all min-h-[40px] ${active ? 'text-white border-transparent shadow-md' : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900'}`}
                style={active ? { backgroundColor: p.color || '#2563eb' } : undefined}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? 'white' : (p.color || '#2563eb') }} />
                {p.name}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active ? 'bg-white/25' : 'bg-zinc-100'}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">
          💡 O preset ativo define quais pratos aparecem no <strong>/delivery</strong>. Itens fora dele ficam ocultos.
        </p>
      </div>

      {isInitialLoading && (
        <div data-testid="menu-loading-skeleton" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-2xl overflow-hidden animate-pulse">
              <div className="h-32 bg-zinc-100" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-zinc-100 rounded w-3/4" />
                <div className="h-3 bg-zinc-100 rounded w-1/2" />
                <div className="h-6 bg-zinc-100 rounded w-1/3 mt-3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isInitialLoading && groupedByDisplay.length === 0 && items.length === 0 && (
        <div className="text-center py-16 text-zinc-300">
          <UtensilsCrossed size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">Nenhum prato cadastrado</p>
          <button onClick={openCreate} className="mt-3 text-sm font-bold text-[#2563eb]">Cadastre o primeiro</button>
        </div>
      )}

      {groupedByDisplay.map((section) => (
        <section key={section.key} data-testid={`display-section-${section.key}`} className="mb-10">
          <div className="flex items-baseline justify-between mb-4 border-b-2 border-zinc-200 pb-2">
            <h3 className="font-heading text-2xl font-black text-zinc-900 tracking-tight">
              {section.label}
            </h3>
            <span className="font-mono text-xs text-zinc-500 font-bold">
              {section.items.length} {section.items.length === 1 ? 'item' : 'itens'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {section.items.map((item, idx) => (
              <div key={item.id} data-testid={`admin-item-${item.id}`} className="bg-white border-2 border-zinc-200 rounded-xl overflow-hidden flex flex-col">
                <div className="relative aspect-[16/10] bg-zinc-100">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-300"><ImageIcon size={36} /></div>
                  )}
                  {!item.available && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="bg-white text-zinc-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Indisponível</span>
                    </div>
                  )}
                  {item.servings > 0 && (
                    <div
                      data-testid={`servings-badge-${item.id}`}
                      className="absolute top-2 right-2 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm border border-zinc-200 shadow-md text-[11px] font-bold text-zinc-800"
                      title={`Rendimento: serve ${item.servings} ${item.servings === 1 ? 'pessoa' : 'pessoas'}`}
                    >
                      <Users size={11} strokeWidth={2.5} />
                      Serve {item.servings}
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-heading font-bold text-zinc-900">{item.name}</p>
                    <p className="font-mono font-black text-zinc-900 shrink-0">{formatCurrency(item.price)}</p>
                  </div>
                  <p className="text-xs text-zinc-500 line-clamp-2 mb-2">{item.description}</p>
                  {(() => {
                    const itTags = asArray(item.tags);
                    if (itTags.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {itTags.map((t) => {
                          const meta = TAG_OPTIONS.find((o) => o.value === t);
                          if (!meta) return null;
                          const Icon = meta.icon;
                          return <Badge key={t} className={`${meta.color} text-white border-0 text-[10px]`}><Icon size={10} className="mr-1" /> {meta.label.split(' (')[0]}</Badge>;
                        })}
                      </div>
                    );
                  })()}
                  <div className="mt-auto flex items-center justify-between pt-2 border-t border-zinc-100">
                    <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                      <Switch data-testid={`toggle-available-${item.id}`} checked={item.available !== false} onCheckedChange={() => toggleAvailable(item)} />
                      Disponível
                    </label>
                    <div className="flex gap-1">
                      <button
                        data-testid={`move-up-${item.id}`}
                        onClick={() => moveItem(section.items, idx, -1)}
                        disabled={idx === 0}
                        title="Subir posição no cardápio"
                        className="w-9 h-9 rounded-lg border-2 border-zinc-200 hover:border-[#2563eb] hover:text-[#2563eb] disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:border-zinc-200 disabled:hover:text-inherit flex items-center justify-center transition-all"
                      ><ArrowUp size={14} /></button>
                      <button
                        data-testid={`move-down-${item.id}`}
                        onClick={() => moveItem(section.items, idx, 1)}
                        disabled={idx === section.items.length - 1}
                        title="Descer posição no cardápio"
                        className="w-9 h-9 rounded-lg border-2 border-zinc-200 hover:border-[#2563eb] hover:text-[#2563eb] disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:border-zinc-200 disabled:hover:text-inherit flex items-center justify-center transition-all"
                      ><ArrowDown size={14} /></button>
                      <button data-testid={`edit-item-${item.id}`} onClick={() => openEdit(item)} className="w-9 h-9 rounded-lg border-2 border-zinc-200 hover:border-zinc-900 flex items-center justify-center transition-all"><Pencil size={14} /></button>
                      <button data-testid={`delete-item-${item.id}`} onClick={() => setConfirmDelete(item)} className="w-9 h-9 rounded-lg border-2 border-zinc-200 hover:border-red-500 hover:text-red-600 flex items-center justify-center transition-all"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent data-testid="menu-item-dialog" className="max-w-xl bg-white border-2 border-zinc-900 rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">{editing?.isNew ? 'Novo Prato' : 'Editar Prato'}</DialogTitle>
            <DialogDescription>As alterações refletem instantaneamente no cardápio do cliente.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Image preview + upload */}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Foto do Prato</label>
              <div className="aspect-[16/10] bg-zinc-100 rounded-xl overflow-hidden border-2 border-zinc-200 mb-2 relative">
                {form.imageUrl ? (
                  <img data-testid="image-preview" src={form.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-zinc-300">
                    <ImageIcon size={36} />
                    <p className="text-xs mt-1">Sem foto</p>
                  </div>
                )}
                {isBase64Image(form.imageUrl) && (
                  <Badge className="absolute top-2 left-2 bg-emerald-500 text-white border-0 text-[10px]">Upload local</Badge>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  data-testid="image-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                />
                <button
                  data-testid="image-upload-btn"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#2563eb] text-white rounded-lg font-bold py-2.5 text-sm min-h-[40px] active:translate-y-[1px] transition-all disabled:opacity-50"
                >
                  <Upload size={14} />
                  {uploading
                    ? (uploadProgress?.stage === 'uploading'
                        ? `Enviando... ${uploadProgress.pct}%`
                        : uploadProgress?.stage === 'optimizing'
                          ? 'Otimizando foto...'
                          : 'Processando...')
                    : 'Escolher foto da galeria'}
                </button>
                {form.imageUrl && (
                  <button type="button" onClick={() => setForm({ ...form, imageUrl: '' })} className="px-3 rounded-lg border-2 border-zinc-200 hover:border-red-400 hover:text-red-600 text-sm font-bold text-zinc-600 transition-all">
                    Remover
                  </button>
                )}
              </div>
              <Input
                data-testid="image-url-input"
                value={isBase64Image(form.imageUrl) ? '' : form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                placeholder="Ou cole uma URL (https://...)"
                className="mt-2 h-10 border-2 border-zinc-200 rounded-lg text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Nome</label>
                <Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Quentinha de Carne" className="h-11 border-2 border-zinc-200 rounded-lg" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Preço (R$)</label>
                <Input data-testid="item-price-input" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="18.00" className="h-11 border-2 border-zinc-200 rounded-lg font-mono" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Categoria</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, servings: v === 'Família' ? form.servings : '' })}>
                <SelectTrigger data-testid="item-category-select" className="h-11 border-2 border-zinc-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.category === 'Família' && (
              <div data-testid="servings-field">
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 flex items-center gap-1.5">
                  <Users size={12} strokeWidth={2.5} /> Rendimento (pessoas)
                  <span className="font-normal lowercase tracking-normal text-zinc-400">— opcional</span>
                </label>
                <Input
                  data-testid="item-servings-input"
                  type="number"
                  step="1"
                  min="1"
                  value={form.servings}
                  onChange={(e) => setForm({ ...form, servings: e.target.value })}
                  placeholder="Ex.: 3 (serve 3 pessoas)"
                  className="h-11 border-2 border-zinc-200 rounded-lg font-mono w-full sm:w-1/2"
                />
                <p className="text-[11px] text-zinc-400 mt-1">Mostra um selo &quot;Serve N pessoas&quot; no card e o preço por pessoa no cardápio do cliente.</p>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Descrição</label>
              <Textarea data-testid="item-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Arroz, feijão, carne, salada" rows={2} className="border-2 border-zinc-200 rounded-lg text-sm" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-2 block">Tags / Badges</label>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map((opt) => {
                  const active = form.tags.includes(opt.value);
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      data-testid={`tag-toggle-${opt.value}`}
                      type="button"
                      onClick={() => toggleTag(opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${active ? `${opt.color} text-white border-transparent` : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-900'}`}
                    >
                      <Icon size={12} /> {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">💡 A tag <strong>Sugestão do Chef</strong> marca este item como cross-sell automático no delivery.</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-2 block">Modos de Operação (Presets)</label>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => {
                  const active = form.presets.includes(p.name);
                  return (
                    <button
                      key={p.id}
                      data-testid={`preset-toggle-${p.name}`}
                      type="button"
                      onClick={() => togglePresetInForm(p.name)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${active ? 'text-white border-transparent' : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-900'}`}
                      style={active ? { backgroundColor: p.color || '#2563eb' } : undefined}
                    >
                      <Layers size={12} /> {p.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">Este prato aparecerá no delivery apenas nos presets marcados.</p>
            </div>

            <label className="flex items-center gap-3 pt-2 border-t border-zinc-100">
              <Switch data-testid="item-available-switch" checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} />
              <div>
                <p className="text-sm font-bold text-zinc-900">Disponível agora</p>
                <p className="text-xs text-zinc-500">Desmarque para ocultar do delivery temporariamente</p>
              </div>
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={closeDialog} className="px-5 py-2.5 rounded-lg border-2 border-zinc-300 font-bold text-zinc-600 hover:border-zinc-900 transition-all min-h-[40px]">Cancelar</button>
            <button data-testid="save-item-btn" onClick={handleSave} className="px-5 py-2.5 rounded-lg bg-[#2563eb] text-white font-bold hover:bg-[#1d4ed8] transition-all min-h-[40px]">
              Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="max-w-sm bg-white border-2 border-zinc-900 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Excluir prato?</DialogTitle>
            <DialogDescription>"{confirmDelete?.name}" será removido do cardápio.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border-2 border-zinc-300 font-bold text-zinc-600 hover:border-zinc-900 transition-all">Cancelar</button>
            <button data-testid="confirm-delete-btn" onClick={handleDelete} className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 transition-all">Excluir</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de troca de preset (Fase 5) */}
      <Dialog open={!!pendingPreset} onOpenChange={(open) => { if (!open) setPendingPreset(null); }}>
        <DialogContent data-testid="preset-confirm-dialog" className="max-w-md bg-white border-2 border-zinc-900 rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle size={22} className="text-amber-600" />
              </div>
              <DialogTitle className="font-heading text-lg font-bold">Trocar modo de operação?</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-zinc-600">
              Você está prestes a mudar de <strong>"{activePreset}"</strong> para <strong>"{pendingPreset}"</strong>.
              Isso altera imediatamente quais pratos aparecem no delivery. Pedidos em andamento não são afetados.
            </DialogDescription>
          </DialogHeader>
          {pendingPreset && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 my-2">
              <p className="text-xs text-zinc-500">Itens que ficarão visíveis em "{pendingPreset}":</p>
              <p className="font-mono text-sm font-bold text-zinc-900 mt-1">
                {items.filter((i) => asArray(i.presets, ['Padrão']).includes(pendingPreset)).length} pratos
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <button data-testid="preset-cancel-btn" onClick={() => setPendingPreset(null)} className="px-4 py-2 rounded-lg border-2 border-zinc-300 font-bold text-zinc-600 hover:border-zinc-900 transition-all">Cancelar</button>
            <button data-testid="preset-confirm-btn" onClick={confirmPresetSwitch} className="px-4 py-2 rounded-lg bg-[#2563eb] text-white font-bold hover:bg-[#1d4ed8] transition-all">
              Ativar "{pendingPreset}"
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Preset Manager Dialog (Fase 10 - CRUD) */}
      <Dialog open={showPresetManager} onOpenChange={setShowPresetManager}>
        <DialogContent data-testid="preset-manager-dialog" className="max-w-lg bg-white border border-zinc-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Gerenciar Presets</DialogTitle>
            <DialogDescription>
              Crie novos modos de operação (ex: Feriado, Noite de Caldos) ou remova os que não usa mais.
              Presets padrão do sistema não podem ser removidos.
            </DialogDescription>
          </DialogHeader>

          {/* Criar novo */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 mt-2">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Criar Novo Preset</p>
            <div className="flex gap-2">
              <Input
                data-testid="new-preset-input"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                placeholder="Ex: Feriado, Noite de Caldos"
                maxLength={30}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreatePreset(); } }}
                className="flex-1"
              />
              <button
                data-testid="create-preset-btn"
                onClick={handleCreatePreset}
                disabled={!newPresetName.trim()}
                className="px-4 h-10 rounded-lg bg-[#2563eb] text-white text-sm font-bold hover:bg-[#1d4ed8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={15} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Lista de presets existentes */}
          <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto">
            {presets.map((p) => {
              const itemCount = items.filter((i) => asArray(i.presets, ['Padrão']).includes(p.name)).length;
              const isRenaming = renamingPreset?.id === p.id;
              return (
                <div
                  key={p.id}
                  data-testid={`preset-row-${p.name}`}
                  className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl p-3"
                >
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: p.color || '#2563eb' }}
                  />
                  {isRenaming ? (
                    <Input
                      data-testid={`rename-preset-input-${p.name}`}
                      autoFocus
                      value={renamingPreset.newName}
                      onChange={(e) => setRenamingPreset({ ...renamingPreset, newName: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleRenamePreset(); }
                        if (e.key === 'Escape') setRenamingPreset(null);
                      }}
                      maxLength={30}
                      className="flex-1 h-9"
                    />
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-zinc-900 truncate">
                        {p.name}
                        {p.protected && (
                          <span className="ml-2 text-[9px] uppercase tracking-wider font-bold text-zinc-400">padrão</span>
                        )}
                      </p>
                      <p className="text-[11px] text-zinc-500">{itemCount} prato{itemCount === 1 ? '' : 's'}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-1 shrink-0">
                    {isRenaming ? (
                      <>
                        <button
                          data-testid={`rename-save-${p.name}`}
                          onClick={handleRenamePreset}
                          className="h-8 px-3 rounded-lg bg-[#2563eb] text-white text-xs font-bold hover:bg-[#1d4ed8]"
                        >
                          Salvar
                        </button>
                        <button
                          onClick={() => setRenamingPreset(null)}
                          className="w-8 h-8 rounded-lg text-zinc-400 hover:bg-zinc-100 flex items-center justify-center"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          data-testid={`preset-rename-${p.name}`}
                          disabled={p.protected}
                          onClick={() => startRename(p)}
                          className="w-8 h-8 rounded-lg text-zinc-500 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                          title={p.protected ? 'Preset padrão não pode ser renomeado' : 'Renomear'}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          data-testid={`preset-delete-${p.name}`}
                          disabled={p.protected}
                          onClick={() => setDeletingPreset(p)}
                          className="w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                          title={p.protected ? 'Preset padrão não pode ser removido' : 'Remover'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="mt-4">
            <button
              data-testid="preset-manager-close"
              onClick={() => setShowPresetManager(false)}
              className="px-5 h-10 rounded-lg border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50"
            >
              Fechar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de delete de preset */}
      <Dialog open={!!deletingPreset} onOpenChange={(open) => { if (!open) setDeletingPreset(null); }}>
        <DialogContent data-testid="preset-delete-dialog" className="max-w-md bg-white border-2 border-red-200 rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={22} className="text-red-600" />
              </div>
              <DialogTitle className="font-heading text-lg font-bold">Remover preset?</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-zinc-600">
              Você vai remover o preset <strong>"{deletingPreset?.name}"</strong>.
              Itens que pertencem apenas a esse preset serão movidos automaticamente para <strong>"Padrão"</strong>.
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              data-testid="preset-delete-cancel"
              onClick={() => setDeletingPreset(null)}
              className="px-4 py-2 rounded-lg border-2 border-zinc-300 font-bold text-zinc-600 hover:border-zinc-900"
            >
              Cancelar
            </button>
            <button
              data-testid="preset-delete-confirm"
              onClick={confirmDeletePreset}
              className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700"
            >
              Remover
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
